/**
 * The small shared UI vocabulary. Deliberately few pieces: every screen has one
 * obvious primary action, so there is not much to build.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export function Screen({
  title,
  children,
  action,
}: {
  title?: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <main className="mx-auto flex w-full max-w-xl flex-1 flex-col gap-5 px-4 pt-5 pb-28">
      {title ? (
        <header className="flex items-baseline justify-between">
          <h1 className="text-xs font-semibold tracking-[0.18em] text-mist-400 uppercase">
            {title}
          </h1>
          {action}
        </header>
      ) : null}
      {children}
    </main>
  );
}

export function Card({
  children,
  className = '',
  as: Tag = 'section',
}: {
  children: ReactNode;
  className?: string;
  as?: 'section' | 'article' | 'div';
}) {
  return (
    <Tag
      className={`rounded-2xl border border-ink-700 bg-ink-900 p-4 shadow-[0_1px_0_rgba(255,255,255,0.03)_inset] ${className}`}
    >
      {children}
    </Tag>
  );
}

export function Label({ children }: { children: ReactNode }) {
  return (
    <span className="text-[0.68rem] font-semibold tracking-[0.16em] text-mist-400 uppercase">
      {children}
    </span>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  full?: boolean;
};

const VARIANTS: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-signal-500 text-ink-950 hover:bg-signal-400 font-semibold',
  secondary: 'bg-ink-700 text-mist-200 hover:bg-ink-600',
  ghost: 'bg-transparent text-mist-400 hover:text-mist-200',
  danger: 'bg-transparent text-red-300 border border-red-900/60 hover:bg-red-950/40',
};

export function Button({ variant = 'secondary', full, className = '', ...props }: ButtonProps) {
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        VARIANTS[variant]
      } ${full ? 'w-full' : ''} ${className}`}
    />
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-2">
      <Label>{label}</Label>
      {children}
      {hint ? <span className="text-xs text-mist-400">{hint}</span> : null}
    </label>
  );
}

export const inputClass =
  'w-full rounded-xl border border-ink-700 bg-ink-800 px-3 py-3 text-base text-mist-200 outline-none placeholder:text-ink-600 focus:border-signal-500';

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 py-10 text-sm text-mist-400" role="status">
      <span className="h-3 w-3 animate-ping rounded-full bg-signal-500" />
      {label}…
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-xl border border-red-900/60 bg-red-950/30 p-3 text-sm text-red-200"
    >
      {children}
    </p>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Card className="text-center">
      <p className="text-base text-mist-200">{title}</p>
      {children ? <div className="mt-3 text-sm text-mist-400">{children}</div> : null}
    </Card>
  );
}

export function ProgressDots({ total, done }: { total: number; done: number }) {
  return (
    <div className="flex items-center gap-1.5" aria-hidden>
      {Array.from({ length: total }, (_, index) => (
        <span
          key={index}
          className={`h-1.5 flex-1 rounded-full ${index < done ? 'bg-signal-500' : 'bg-ink-700'}`}
        />
      ))}
    </div>
  );
}
